import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { CrematoryListInfo } from '@app/objects/Crematory';
import { ServiceType } from '@app/objects/Pet';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';

export interface ServiceTypeOption {
	label: string;
	value: ServiceType;
}

function toServiceTypes(crematory?: CrematoryListInfo): Array<ServiceType> {
	const result: Array<ServiceType> = [];

	if (crematory?.private?.useServiceType) {
		result.push(ServiceType.Private);
	}

	if (crematory?.semiPrivate?.useServiceType) {
		result.push(ServiceType.SemiPrivate);
	}

	if (crematory?.communal?.useServiceType) {
		result.push(ServiceType.Communal);
	}

	if (crematory?.storage?.useServiceType) {
		result.push(ServiceType.Storage);
	}

	if (crematory?.productsOnly?.useServiceType) {
		result.push(ServiceType.ProductsOnly);
	}

	return result;
}

function getTranslationMap(isAustralia: boolean): Map<ServiceType, string> {
	return new Map<ServiceType, string>([
		[ServiceType.Private, `enums.service-type.${isAustralia ? 'aus-' : ''}private`],
		[ServiceType.SemiPrivate, `enums.service-type.${isAustralia ? 'aus-' : ''}semiprivate`],
		[ServiceType.Communal, 'enums.service-type.communal'],
		[ServiceType.Storage, 'enums.service-type.storage'],
		[ServiceType.ProductsOnly, 'enums.service-type.products-only'],
	]);
}

const exclusiveCrematories: Set<number> = new Set([19]);

export function useServiceTypeNames(crematoryId?: number): Map<ServiceType, string> {
	const crematory = useCrematory(crematoryId);
	const { t } = useTranslation();

	return React.useMemo(() => {
		const crematoryId = crematory?.id ?? 0;

		if (exclusiveCrematories.has(crematoryId)) {
			return new Map<ServiceType, string>([
				[ServiceType.Private, 'Exclusive'],
				[ServiceType.SemiPrivate, 'Individual'],
				[ServiceType.Communal, 'Communal'],
				[ServiceType.Storage, 'Storage'],
				[ServiceType.ProductsOnly, 'Products Only'],
			]);
		}

		const translationMap = getTranslationMap(crematory?.country?.toLowerCase() === 'aus');
		const result = new Map<ServiceType, string>();

		// eslint-disable-next-line no-restricted-syntax
		for (const [key, value] of translationMap) {
			result.set(key, t(value));
		}

		return result;
	}, [t, crematory?.country, crematory?.id]);
}

export function useServiceTypes(crematoryId?: number): Array<ServiceTypeOption> {
	const crematory = useCrematory(crematoryId);
	const map = useServiceTypeNames(crematoryId);

	return React.useMemo(() => toServiceTypes(crematory).map((type: ServiceType) => ({
		value: type,
		label: map.get(type)!,
	})), [crematory, map]);
}
